
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bot, CheckCircle, XCircle, ExternalLink, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { isSystemBotTokenConfigured, getSystemBotInfo } from '@/services/telegram/botTokenService';
import { getSupabaseDashboardUrl } from '@/utils/supabaseUrl';
import { useAuth } from '@/contexts/AuthContext';

interface BotInfo {
  first_name: string;
  username: string;
  id: number;
}

export function SystemBotStatus() {
  const [isConfigured, setIsConfigured] = useState<boolean>(false);
  const [botInfo, setBotInfo] = useState<BotInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isTesting, setIsTesting] = useState<boolean>(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    checkBotStatus();
  }, []);

  const checkBotStatus = async () => {
    setIsLoading(true);
    try {
      const configured = await isSystemBotTokenConfigured();
      setIsConfigured(configured);
      
      if (configured) {
        const info = await getSystemBotInfo();
        setBotInfo(info);
      }
    } catch (error) {
      console.error('Error checking bot status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    try {
      const configured = await isSystemBotTokenConfigured();
      if (configured) {
        const info = await getSystemBotInfo();
        setBotInfo(info);
        setIsConfigured(true);
        
        toast({
          title: "Connection Test Successful",
          description: `Connected to bot: ${info?.first_name} (@${info?.username})`,
          variant: "default"
        });
      } else {
        toast({
          title: "Connection Test Failed",
          description: "Bot token is not configured in the system",
          variant: "destructive"
        });
      }
    } catch (error: any) {
      toast({
        title: "Connection Test Failed",
        description: error.message || "Could not connect to Telegram API",
        variant: "destructive"
      });
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            System Bot Status
          </CardTitle>
          <CardDescription>Loading bot configuration status...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bot className="h-5 w-5" />
          System Bot Status
        </CardTitle>
        <CardDescription>
          Status of the system-wide Telegram bot configuration
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 p-4 rounded-lg border">
          {isConfigured ? (
            <>
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div className="flex-1">
                <h3 className="font-medium text-lg">Bot Configured</h3>
                <p className="text-sm text-muted-foreground">
                  System bot token is properly configured
                </p>
              </div>
            </>
          ) : (
            <>
              <XCircle className="h-8 w-8 text-destructive" />
              <div className="flex-1">
                <h3 className="font-medium text-lg">Bot Not Configured</h3>
                <p className="text-sm text-muted-foreground">
                  System bot token needs to be configured
                </p>
              </div>
            </>
          )}
        </div>

        {botInfo && (
          <div className="p-3 bg-muted rounded-md">
            <h4 className="font-medium mb-2">Bot Information:</h4>
            <div className="text-sm space-y-1">
              <p><strong>Name:</strong> {botInfo.first_name}</p>
              <p><strong>Username:</strong> @{botInfo.username}</p>
              <p><strong>ID:</strong> {botInfo.id}</p>
            </div>
          </div>
        )}

        {!isConfigured && (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Configuration Required</AlertTitle>
            <AlertDescription>
              The system Telegram bot token needs to be configured by an administrator 
              for notifications and integrations to work properly.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-between items-center pt-4">
          <Button 
            variant="outline" 
            onClick={handleTestConnection} 
            disabled={isTesting}
          >
            {isTesting ? 'Testing...' : 'Test Connection'}
          </Button>
          
          {isAdmin && (
            <Button 
              variant="outline" 
              onClick={() => window.open(getSupabaseDashboardUrl('settings/functions'), '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Manage Secrets
            </Button>
          )}
        </div>

        {isAdmin && (
          <Alert>
            <Bot className="h-4 w-4" />
            <AlertTitle>Administrator Note</AlertTitle>
            <AlertDescription>
              To configure the bot token, go to Supabase project settings and add the 
              TELEGRAM_BOT_TOKEN secret. Get your bot token from @BotFather on Telegram.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
